import { Box, Grid, InputAdornment, Typography } from '@mui/material'
import _ from 'lodash'
import moment from 'moment'
import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

/** API */
import { FindAllInterface } from 'api/api-interface'
import ClinicApi, { BranchInterface, FindPatientInterface, PatientInterface } from 'api/master/clinic.api'
import OperativeTypeApi, { OperativeTypeObjInterface } from 'api/setting/treatments/operative-type.api'
import OperativeApi, { OperativeObjInterface } from 'api/setting/treatments/operative.api'
import RecallApi, { CancelRecallInterface } from 'api/setting/treatments/recall.api'

/** TRANSLATION*/
import { useTranslation } from 'react-i18next'

/** UTILS || SLICE */
import { handleShow, resetModal, showModal } from 'app/slice/modal.slice'
import { SORT_TYPE } from 'constants/common'
import { toBuddhistYear } from 'utils/app.utils'
import { isCreate, isDelete, isExport, isUpdate, isView } from 'utils/verification.utils'

/** COMPONENT */
import HeaderCard from 'component/CardCustom/HeaderCard'
import { emptyRangePicker } from 'component/Input/InputSecondNewRangePicker'
import Loading from 'component/Loading'
import { notiError, notiSuccess } from 'component/notifications/notifications'
import TableRowExport from 'component/Pdf/TableRowExport'
import TableRowCommon from 'component/Table/TableRowCommon'
import HistoryRecall, { HistoryRecallInterface } from 'features/counter/recall/history-recall'

/** STYLE */
import { faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import TableCustom from 'component/Table/TableCustom'
import DropdownButton from 'new-components/buttons/dropdown-button'
import MainButton from 'new-components/buttons/main-button'
import DateRangeInput from 'new-components/inputs/date-range-input'
import SelectInput from 'new-components/inputs/select-input'
import TextInput from 'new-components/inputs/text-input'
import AlertModal from 'new-components/modal/alert-modal'
import { exportRecallListsToExcel } from './exportRecallListsToExcel'

const permissions = {
    isCreate: isCreate(),
    isDelete: isDelete(),
    isUpdate: isUpdate(),
    isView: isView(),
    isExport: isExport()
}

interface RecallListsInterface {
    branchCnNumber: string
    branchCode: null | string
    branchId: null | string
    dateOfLastService: string
    dayAmount: number
    doctorFirstname: string
    doctorLastname: string
    firstname: string
    historyRecallId: null | number
    lastRecall: null | string
    lastname: string
    nextRecall: string
    operativeId: number
    operativeName: string
    operativeTypeId: number
    operativeTypeName: string
    patientId: number
    phone: string
    prefixName: string
    recallId: number
    recallUnit: string
    status: string
}

const initialHistoryRecall: HistoryRecallInterface = {
    historyId: 0,
    branchCnNumber: '',
    patientName: '',
    lastService: '',
    operative: '',
    show: false
}

export default function RecallLists() {
    const { t } = useTranslation()
    const dispatch = useDispatch()
    const modal: boolean = useSelector(handleShow)
    const componentRef = useRef<any>(null)
    const [recall, setRecall] = useState<RecallListsInterface[]>([]);
    const [exportData, setExportData] = useState<RecallListsInterface[]>([])
    const [loading, setLoading] = useState<boolean>(false);
    const [numberPage, setNumberPage] = useState<number>(1);

    /** default data */
    const [loadingBranches, setLoadingBranches] = useState<boolean>(false);
    const [loadingPatient, setLoadingPatient] = useState<boolean>(false);
    const [loadingOperativeType, setLoadingOperativeType] = useState<boolean>(false);
    const [loadingOperative, setLoadingOperative] = useState<boolean>(false);
    const [branches, setBranches] = useState<BranchInterface[]>([]);
    const [patients, setPatients] = useState<PatientInterface[]>([]);
    const [operativeTypes, setOperativeTypes] = useState<OperativeTypeObjInterface[]>([]);
    const [operatives, setOperatives] = useState<OperativeObjInterface[]>([]);
    const [openPopupHistory, setOpenPopupHistory] = useState<HistoryRecallInterface>(initialHistoryRecall);

    /** pagination */
    const [page, setPage] = useState<number>(1)
    const [pageLimit, setPageLimit] = useState<number>(10)
    const [rowCount, setRowCount] = useState<number>(0)
    const [sortType, setSortType] = useState<SORT_TYPE>(SORT_TYPE.DESC)
    const [sortBy, setSortBy] = useState<string>('nextRecall')
    const [search, setSearch] = useState<string>('')
    const [branchId, setBranchId] = useState<number>(0);
    const [patientId, setPatientId] = useState<number>(0);
    const [operativeTypeId, setOperativeTypeId] = useState<number>(0);
    const [operativeId, setOperativeId] = useState<number>(0);
    const [rangeDate, setRangeDate] = useState<string[]>([
        moment().format('YYYY-MM-DD'),
        moment().add(1, 'month').format('YYYY-MM-DD')
    ]);

    const [isOpenAlert, setIsOpenAlert] = useState(false)
    const [alertTitle, setAlertTitle] = useState('')
    const [alertMessage, setAlertMessage] = useState('')
    const [alertType, setAlertType] = useState<'warning' | 'error'>('warning')
    const [pendingCancelPayload, setPendingCancelPayload] = useState<CancelRecallInterface | null>(null)

    // const handleInputSearch = useCallback(
    //     _.debounce((type: 'search' | 'branch' | 'patient' | 'operativeType' | 'operative', searchText?: string) => {
    //         if (type === 'search') {
    //             loadRecalls(searchText)
    //             loadDataForExport(searchText)
    //         }
    //         if (type === 'branch') loadBranches(searchText)
    //         if (type === 'patient') loadPatients(searchText)
    //         if (type === 'operativeType') loadOperativeTypes(searchText)
    //         if (type === 'operative') loadOperatives(searchText)
    //     }, 1000), []);

    const loadBranches = (search?: string) => {
        let condition: { search?: string } = {}
        if (search) condition = { ...condition, search: search }
        setLoadingBranches(true)
        ClinicApi.findAllBranches(condition).then(({ data }) => setBranches(data)).catch(() => { return }).finally(() => setLoadingBranches(false))
    }

    const loadPatients = (search?: string) => {
        let condition: FindPatientInterface = { pageLimit: 10, allBranch: true, condition: 'cnNumber' }
        if (search) condition = { ...condition, search: search }
        setLoadingPatient(true)
        ClinicApi.findAllPatients(condition).then(({ data }) => setPatients(data)).catch(() => { return }).finally(() => setLoadingPatient(false))
    }

    const loadOperativeTypes = (search?: string) => {
        let condition: FindAllInterface = { pageLimit: 30 }
        if (search) condition = { ...condition, search: search }
        setLoadingOperativeType(true)
        OperativeTypeApi.findAll(condition).then(({ data }) => setOperativeTypes(data)).catch(() => { return }).finally(() => setLoadingOperativeType(false))
    }

    const loadOperatives = (search?: string) => {
        let condition: FindAllInterface = { pageLimit: 10 }
        if (search) condition = { ...condition, search: search }
        setLoadingOperative(true)
        OperativeApi.findAll(condition).then(({ data }) => setOperatives(data)).catch(() => { return }).finally(() => setLoadingOperative(false))
    }

    const loadRecalls = async () => {
        setLoading(true)
        try {
            await RecallApi.findAllList({ page: page, pageLimit: pageLimit, sortBy: sortBy, sortType: sortType, search: search, branchId: branchId || null, patientId: patientId || null, operativeTypeId: operativeTypeId || null, operativeId: operativeId || null, dateStart: rangeDate[0], dateEnd: rangeDate[1] })
                .then(({ data, headers }) => {
                    setRecall(data)
                    setRowCount(Number(headers['x-total']))
                    setNumberPage(page)
                })
                .catch(() => setRecall([]))
                .finally(() => setLoading(false))
        } catch (error) {
            notiError('เกิดข้อผิดพลาด')
        }
    }

    useEffect(() => {
        loadRecalls()
    }, [page, pageLimit, sortBy, sortType])

    const loadDataForExport = async () => {
        try {
            await RecallApi.findAllList({ export: 1, page: 1, pageLimit: 1000, sortBy: sortBy, sortType: sortType, search: search, branchId: branchId || null, patientId: patientId || null, operativeTypeId: operativeTypeId || null, operativeId: operativeId || null, dateStart: rangeDate[0], dateEnd: rangeDate[1] })
                .then(({ data }) => {
                    setExportData(data)
                })
                .catch(() => setExportData([]))
        } catch (error) {
            notiError('เกิดข้อผิดพลาด')
        }
    }

    useEffect(() => {
        loadDataForExport()
    }, [page, pageLimit, sortBy, sortType])

    const headCells = [
        { id: 'id', disablePadding: false, label: t('TABLE.NO'), align: 'center', width: '70px' },
        { id: 'branchCode', disablePadding: false, label: t('RECALL_LISTS.TABLE.CELLS.BRANCH_CODE'), class: 'text-nowrap', width: '125px', sortable: true },
        { id: 'dateOfLastService', disablePadding: false, label: t('RECALL_LISTS.TABLE.CELLS.DATE_OF_LAST_SERVICE'), class: 'text-nowrap', width: '236px', sortable: true },
        { id: 'patientCode', disablePadding: false, label: t('RECALL_LISTS.TABLE.CELLS.PATIENT_CODE'), class: 'text-nowrap' },
        { id: 'patientName', disablePadding: false, label: t('RECALL_LISTS.TABLE.CELLS.PATIENT_NAME'), class: 'text-nowrap' },
        { id: 'phone', disablePadding: false, label: t('RECALL_LISTS.TABLE.CELLS.PHONE'), class: 'text-nowrap', width: '200px' },
        { id: 'operativeTypeName', disablePadding: false, label: t('RECALL_LISTS.TABLE.CELLS.OPERATIVE_TYPE_NAME'), class: 'text-nowrap', sortable: true },
        { id: 'operativeName', disablePadding: false, label: t('RECALL_LISTS.TABLE.CELLS.OPERATIVE_NAME'), class: 'text-nowrap', sortable: true },
        { id: 'doctor', disablePadding: false, label: t('RECALL_LISTS.TABLE.CELLS.DOCTOR'), class: 'text-nowrap' },
        { id: 'duration', disablePadding: false, label: t('RECALL_LISTS.TABLE.CELLS.DURATION'), class: 'text-nowrap', width: '200px' },
        { id: 'nextRecall', disablePadding: false, label: t('RECALL_LISTS.TABLE.CELLS.DATE'), class: 'text-nowrap', width: '200px', sortable: true },
        { id: 'date', disablePadding: false, label: t('RECALL_LISTS.TABLE.CELLS.NEXT_RECALL'), class: 'text-nowrap', width: '200px', },
        { id: 'action', disablePadding: false, label: t('Action'), class: 'text-nowrap', width: '80px' }
    ]

    const filter = [
        { label: `${t('INPUT.SEARCH')}: ${search || '-'}` },
        { label: `${t('RECALL_LISTS.FILTER.BRANCH')}: ${_.get(_.find(branches, { branchId: branchId }), 'branchName') || t('STATUS.ALL')}` },
        { label: `${t('RECALL_LISTS.FILTER.FILTER_PATIENT')}: ${_.get(_.find(patients, { patientId: patientId }), 'fullname') || t('STATUS.ALL')}` },
        { label: `${t('RECALL_LISTS.FILTER.OPERATIVE_TYPE')}: ${_.get(_.find(operativeTypes, { operativeTypeId: operativeTypeId }), 'operativeTypeName') || t('STATUS.ALL')}` },
        { label: `${t('RECALL_LISTS.FILTER.OPERATIVE')}: ${_.get(_.find(operatives, { operativeId: operativeId }), 'operativeName') || t('STATUS.ALL')}` },
        { label: `${t('RECALL_LISTS.FILTER.RANGE_DATE')}: ${(rangeDate[0] && rangeDate[1]) ? `${toBuddhistYear(moment(rangeDate[0]))}-${toBuddhistYear(moment(rangeDate[1]))}` : '-'}` }
    ]

    const renderData = (objData: RecallListsInterface, no: number, exportDataValue: boolean) => {
        const { historyRecallId, branchCode, dateOfLastService, branchCnNumber, firstname, lastname, phone, operativeTypeName, operativeName, doctorFirstname, doctorLastname, dayAmount, recallUnit, lastRecall, nextRecall, status } = objData

        no = numberPage * pageLimit - pageLimit + no + 1

        const objRenderData = {
            key: String(Math.random()),
            id: String(historyRecallId),
            obj: objData,
            columns: [
                { option: 'TEXT', align: 'center', label: no, class: 'px-1' },
                { option: 'TEXT', align: 'left', label: branchCode || '-' },
                { option: 'TEXT', align: 'left', label: dateOfLastService ? `${toBuddhistYear(moment(dateOfLastService), 'DD/MM/YYYY HH:mm')} ${t('TIME_UNIT')}` : '-' },
                { option: 'TEXT', align: 'left', label: `${branchCode || ''}${branchCnNumber || '-'}` },
                { option: 'TEXT', align: 'left', label: `${firstname} ${lastname}` },
                { option: 'TEXT', align: 'left', label: phone || '-' },
                { option: 'TEXT', align: 'left', label: operativeTypeName || '-' },
                { option: 'TEXT', align: 'left', label: operativeName || '-' },
                { option: 'TEXT', align: 'left', label: `${doctorFirstname} ${doctorLastname}` },
                { option: 'TEXT', align: 'left', label: `${dayAmount} ${t(`TIME_DURATION_TYPE.${recallUnit}`)}` },
                { option: 'TEXT', align: 'left', label: status === 'NORMAL' && (nextRecall ? `${toBuddhistYear(moment(`${nextRecall}`), 'DD/MM/YYYY')} 07:00 ${t('TIME_UNIT')}` : '-') || t('STATUS_NO_ICON.INACTIVE') },
                { option: 'TEXT', align: 'left', label: status === 'NORMAL' && (lastRecall ? `${toBuddhistYear(moment(lastRecall), 'DD/MM/YYYY HH:MM')} ${t('TIME_UNIT')}` : '-') || t('STATUS_NO_ICON.INACTIVE') },
                {
                    option: 'ACTION',
                    align: 'center',
                    label: 'action',
                    values: [
                        { option: 'STATUS_ACTIVE', label: t(`RECALL_LISTS.BUTTON.HISTORY`), disabled: false },
                        { option: 'DIVIDER', label: '', disabled: false },
                        { option: 'STATUS_INACTIVE', label: t(`RECALL_LISTS.BUTTON.CANCEL`), disabled: permissions.isUpdate.disabled || status !== 'NORMAL' },
                    ]
                }
            ]
        }

        return (
            !exportDataValue && (
                <TableRowCommon {...objRenderData} onactive={() => onShowHistoryRecall(objData)} oninactive={() => onCancelRecall(objData)} />
            ) || <TableRowExport {...objRenderData} />
        )
    }

    const onShowHistoryRecall = (data: RecallListsInterface) => {
        setOpenPopupHistory({
            historyId: data.historyRecallId || 0,
            branchCnNumber: `${data.branchCode || ''}${data.branchCnNumber || ''}`,
            patientName: `${data.firstname || ''} ${data.lastname || ''}`,
            lastService: data.dateOfLastService ? `${toBuddhistYear(moment(data.dateOfLastService), 'DD/MM/YYYY HH:mm')} ${t('TIME_UNIT')}` : '',
            operative: data.operativeName || '',
            show: true,
        })
        dispatch(showModal())
    }


    const onRequestSort = (sortByVal: string, sortTypeVal: SORT_TYPE) => {
        setSortType(sortTypeVal)
        setSortBy(sortByVal)
        setPage(1)
    }

    const handleChangeRowsPerPage = (limit: number) => {
        setPageLimit(limit)
        setPage(1)
    }

    const handleChangePage = (val: number) => {
        setPage(val)
    }

    useEffect(() => {
        loadBranches()
        loadPatients()
        loadOperativeTypes()
        loadOperatives()
    }, []);

    useEffect(() => {
        if (!modal) {
            setOpenPopupHistory(initialHistoryRecall)
            dispatch(resetModal())
        }
    }, [modal]);

    const onCancelRecall = (data: RecallListsInterface) => {
        const payload: CancelRecallInterface = {
            branchId: Number(data.branchId) || 0,
            operativeId: Number(data.operativeId),
            patientId: Number(data.patientId),
            recallId: Number(data.recallId)
        }

        if (!payload.branchId) return notiError(t('RECALL_LISTS.MESSAGE.ERROR.NO_BRANCH'))
        if (!payload.operativeId) return notiError(t('RECALL_LISTS.MESSAGE.ERROR.NO_OPERATIVE'))
        if (!payload.patientId) return notiError(t('RECALL_LISTS.MESSAGE.ERROR.NO_PATIENT'))
        if (!payload.recallId) return notiError(t('RECALL_LISTS.MESSAGE.ERROR.NO_RECALL'))

        setPendingCancelPayload(payload)
        setAlertTitle(t('RECALL_LISTS.MESSAGE.WARNING.TITLE'))
        setAlertMessage(t('RECALL_LISTS.MESSAGE.WARNING.SUBTITLE'))
        setAlertType('error')
        setIsOpenAlert(true)
    }

    const handleConfirmCancel = () => {
        if (!pendingCancelPayload) return
        setIsOpenAlert(false)
        setLoading(true)
        RecallApi.cancelRecall(pendingCancelPayload)
            .then(() => {
                notiSuccess(t('RECALL_LISTS.MESSAGE.SUCCESS.CANCEL_RECALL'))
                // รีโหลดตาราง
                loadRecalls()
            })
            .catch(() => {
                notiError(t('RECALL_LISTS.MESSAGE.ERROR.DEFAULT'))
            })
            .finally(() => {
                setLoading(false)
                setPendingCancelPayload(null)
            })
    }

    return (
        <Box>
            <AlertModal
                isOpen={isOpenAlert}
                title={alertTitle}
                message={alertMessage}
                type={alertType}
                handleClose={() => setIsOpenAlert(false)}
                onCancel={{ onClick: () => setIsOpenAlert(false), title: t('BUTTON.CANCEL') }}
                onSubmit={{ onClick: handleConfirmCancel, title: t('BUTTON.SUBMIT') }}
            />
            <HeaderCard text={t('RECALL_LISTS.HEADER')} />
            <Box paddingX="24px" paddingTop="16px">
                <Grid container spacing="16px" pb={1}>
                    <Grid item xs={12} sm={6} md={3} xl={2}>
                        <TextInput
                            label={t('RECALL_LISTS.SEARCH')}
                            value={search}
                            onChange={(event) => {
                                setSearch(event.target.value)
                                // handleInputSearch('search', event.target.value)
                            }}
                            fullWidth
                            inputProps={{
                                endAdornment: (
                                    <InputAdornment position="end" >
                                        {!search ? <FontAwesomeIcon icon={faSearch} style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 15 }} /> :
                                            <FontAwesomeIcon
                                                icon={faTimes}
                                                onClick={() => {
                                                    setSearch('');
                                                }}
                                                style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: 17, cursor: 'pointer' }} />}
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} xl={2}>
                        <SelectInput
                            label={t('RECALL_LISTS.FILTER.BRANCH')}
                            value={branchId || 0}
                            onChange={e => {
                                setBranchId(Number(e.target.value))
                            }}
                            options={[
                                { value: 0, name: t('STATUS.ALL') },
                                ...branches.map(b => ({
                                    value: b.branchId,
                                    name: b.branchCode || b.branchName
                                }))
                            ]}
                            width="100%"
                            onClear
                            loading={loadingBranches}
                        />

                    </Grid>
                    <Grid item xs={12} sm={6} md={3} xl={2}>
                        <SelectInput
                            label={t('RECALL_LISTS.FILTER.PATIENT')}
                            value={patientId || 0}
                            onChange={e => {
                                setPatientId(Number(e.target.value))
                            }}
                            options={[
                                { value: 0, name: t('STATUS.ALL') },
                                ...patients.map((p: PatientInterface) => ({
                                    value: p.patientId,
                                    name: p.fullname,
                                }))
                            ]}
                            width="100%"
                            loading={loadingPatient}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} xl={2}>
                        <SelectInput
                            label={t('RECALL_LISTS.FILTER.OPERATIVE_TYPE')}
                            value={operativeTypeId || 0}
                            onChange={e => {
                                setOperativeTypeId(Number(e.target.value))
                            }}
                            options={[
                                { value: 0, name: t('STATUS.ALL') },
                                ...operativeTypes.map((o: OperativeTypeObjInterface) => ({
                                    value: o.operativeTypeId,
                                    name: o.operativeTypeName,
                                }))
                            ]}
                            width="100%"
                            loading={loadingOperativeType}
                        />
                    </Grid>
                </Grid>
                <Grid container spacing="16px">
                    <Grid item xs={12} sm={6} md={3} xl={2} className="w-100">
                        <SelectInput
                            label={t('RECALL_LISTS.FILTER.OPERATIVE')}
                            value={operativeId || 0}
                            onChange={e => {
                                setOperativeId(Number(e.target.value))
                            }}
                            options={[
                                { value: 0, name: t('STATUS.ALL') },
                                ...operatives.map((o: OperativeObjInterface) => ({
                                    value: o.operativeId,
                                    name: o.operativeName,
                                }))
                            ]}
                            width="100%"
                            loading={loadingOperative}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3} xl={2} className='w-100'>
                        <DateRangeInput
                            value={rangeDate || emptyRangePicker}
                            allowClear
                            onchange={(val: any[]) => {
                                if (_.isEmpty(val)) {
                                    setRangeDate(emptyRangePicker)
                                } else {
                                    const [start, end] = val
                                    setRangeDate([
                                        moment(start).format('YYYY-MM-DD'),
                                        moment(end).format('YYYY-MM-DD'),
                                    ])
                                }
                            }}
                            onClear={() => {
                                setRangeDate(emptyRangePicker)
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={12} md={6} xl={8}>
                        <Box display={'flex'} justifyContent={'space-between'}>
                            <MainButton
                                title={t('INPUT.SEARCH')}
                                type='primary'
                                variant='contained'
                                style={{ width: '120px' }}
                                onClick={() => {
                                    setPage(1)
                                    loadRecalls()
                                    loadDataForExport()
                                }} />
                            <Box display="flex" flexDirection="row" alignItems="center" gap="16px">
                                <DropdownButton title={'พิมพ์เอกสาร'} onChange={(e) => {
                                    if (e === 'Excel') {

                                        const report: any = {
                                            title: t('RECALL_LISTS.HEADER'),
                                            filters: `${filter[1].label}   ${filter[2].label}   ${filter[3].label}   ${filter[4].label}   ${filter[5].label} `,
                                            rows: exportData.map(item => ({
                                                branchCode: item.branchCode || '',
                                                branchCnNumber: item.branchCnNumber || '',
                                                dateOfLastService: item.dateOfLastService,
                                                patientCode: `${item.branchCode || ''}${item.branchCnNumber || ''}`,
                                                patientName: `${item.firstname} ${item.lastname}`,
                                                phone: item.phone,
                                                operativeTypeName: item.operativeTypeName,
                                                operativeName: item.operativeName,
                                                doctorName: `${item.doctorFirstname} ${item.doctorLastname}`,
                                                duration: item.dayAmount,
                                                recallUnit: t(`TIME_DURATION_TYPE.${item?.recallUnit}`),
                                                nextRecall: item?.status === 'NORMAL' && (item?.nextRecall ? `${toBuddhistYear(moment(item?.nextRecall), 'DD/MM/YYYY')} 07:00 ${t('TIME_UNIT')}` : '-') || t('STATUS_NO_ICON.INACTIVE'),
                                                lastRecall: item?.status === 'NORMAL' && (item?.lastRecall ? `${toBuddhistYear(moment(item?.lastRecall), 'DD/MM/YYYY HH:MM')} ${t('TIME_UNIT')}` : '-') || t('STATUS_NO_ICON.INACTIVE'),
                                                status: item.status
                                            })),
                                            t
                                        }
                                        exportRecallListsToExcel(report)
                                    } else if (e === 'PDF') {
                                        const style = document.createElement("style");
                                        style.innerHTML = `
                                      @media print {
                                        @page { size: landscape; }
                                       }
                                     `;
                                        document.head.appendChild(style);
                                        window.print();
                                        document.head.removeChild(style);

                                    }
                                }} options={[{ value: 'Excel', name: ' Excel' }, { value: 'PDF', name: ' PDF' }]} />
                            </Box>
                        </Box>
                    </Grid>
                </Grid>
                <Box paddingTop="24px">
                    <TableCustom
                        tableFixedWidth
                        tableMinWidth={2500}
                        page={page}
                        pageLimit={pageLimit}
                        sortType={sortType}
                        sortBy={sortBy}
                        rowCount={rowCount}
                        onSort={onRequestSort}
                        setPageLimit={handleChangeRowsPerPage}
                        setPage={handleChangePage}
                        headCells={headCells}
                        rowsData={_.map(recall, (val: RecallListsInterface, i) => {
                            return renderData(val, i, false)
                        })}
                    />
                </Box>
            </Box>
            {openPopupHistory.show && (
                <HistoryRecall
                    show={openPopupHistory.show}
                    historyId={openPopupHistory.historyId}
                    branchCnNumber={openPopupHistory.branchCnNumber}
                    patientName={openPopupHistory.patientName}
                    lastService={openPopupHistory.lastService}
                    operative={openPopupHistory.operative} />
            )}
            <Loading show={loading} type={'fullLoading'} />

            <Box
                id="print-table-container"
                ref={componentRef}
            >
                <Typography fontSize="14px" fontWeight={400}>{t('RECALL_LISTS.HEADER')}</Typography>
                <Box display='flex' flexDirection="row" alignItems="center" gap="16px">
                    <Typography fontSize="10px" fontWeight={400}>{`${t('RECALL_LISTS.FILTER.BRANCH')}: ${_.get(_.find(branches, { branchId: branchId }), 'branchName') || t('STATUS.ALL')}`}</Typography>
                    <Typography fontSize="10px" fontWeight={400}>{`${t('RECALL_LISTS.FILTER.FILTER_PATIENT')}: ${_.get(_.find(patients, { patientId: patientId }), 'fullname') || t('STATUS.ALL')}`}</Typography>
                    <Typography fontSize="10px" fontWeight={400}>{`${t('RECALL_LISTS.FILTER.OPERATIVE_TYPE')}: ${_.get(_.find(operativeTypes, { operativeTypeId: operativeTypeId }), 'operativeTypeName') || t('STATUS.ALL')}`}</Typography>
                    <Typography fontSize="10px" fontWeight={400}>{`${t('RECALL_LISTS.FILTER.OPERATIVE')}: ${_.get(_.find(operatives, { operativeId: operativeId }), 'operativeName') || t('STATUS.ALL')}`}</Typography>
                    <Typography fontSize="10px" fontWeight={400}>{`${t('RECALL_LISTS.FILTER.RANGE_DATE')}: ${(rangeDate[0] && rangeDate[1]) ? `${toBuddhistYear(moment(rangeDate[0]))}-${toBuddhistYear(moment(rangeDate[1]))}` : '-'}`}</Typography>
                </Box>
                <table style={{ width: '100%', border: '1px solid black', borderCollapse: 'collapse', marginTop: '16px' }}>
                    <thead>
                        <tr style={{ textAlign: 'center', fontSize: '9.5px', fontWeight: 700 }}>
                            <th style={{ borderBottom: '1px solid black' }}>{t('TABLE.NO')}</th>
                            <th style={{ borderBottom: '1px solid black', textAlign: 'center' }}>{t('RECALL_LISTS.TABLE.CELLS.BRANCH_CODE')}</th>
                            <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('RECALL_LISTS.TABLE.CELLS.DATE_OF_LAST_SERVICE')}</th>
                            <th style={{ borderBottom: '1px solid black', textAlign: 'center' }}>{t('RECALL_LISTS.TABLE.CELLS.PATIENT_CODE')}</th>
                            <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('RECALL_LISTS.TABLE.CELLS.PATIENT_NAME')}</th>
                            <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('RECALL_LISTS.TABLE.CELLS.PHONE')}</th>
                            <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('RECALL_LISTS.TABLE.CELLS.OPERATIVE_TYPE_NAME')}</th>
                            <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('RECALL_LISTS.TABLE.CELLS.OPERATIVE_NAME')}</th>
                            <th style={{ borderBottom: '1px solid black', textAlign: 'left' }}>{t('RECALL_LISTS.TABLE.CELLS.DOCTOR')}</th>
                            <th style={{ borderBottom: '1px solid black', textAlign: 'center' }}>{t('RECALL_LISTS.TABLE.CELLS.DURATION')}</th>
                            <th style={{ borderBottom: '1px solid black', textAlign: 'center' }}>{t('RECALL_LISTS.TABLE.CELLS.DATE')}</th>
                            <th style={{ borderBottom: '1px solid black' }}>{t('RECALL_LISTS.TABLE.CELLS.NEXT_RECALL')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {exportData?.length === 0 &&
                            <tr>
                                <td colSpan={12} style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '100%' }}>{t('NOT_FOUND')}</td>
                            </tr>
                        }
                        {exportData?.map((data: any, index: number) => (
                            <tr key={index}>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '50px' }}>{index + 1}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: '50px' }}>{data?.branchCode || '-'}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'center', width: '120px' }}>{data?.dateOfLastService ? `${toBuddhistYear(moment(data?.dateOfLastService), 'DD/MM/YYYY HH:mm')} ${t('TIME_UNIT')}` : "-"}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: "100px" }}>{`${data?.branchCode || ''}${data?.branchCnNumber || '-'}`}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'center' }}>{`${data?.firstname} ${data?.lastname}`}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'center', width: "50px" }}>{data?.phone || '-'}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'center' }}>{data?.operativeTypeName || '-'}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'center' }}>{data?.operativeName || '-'}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'left', verticalAlign: 'center' }}>{`${data?.doctorFirstname} ${data?.doctorLastname}`}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: "75px" }}>{`${data?.dayAmount} ${t(`TIME_DURATION_TYPE.${data?.recallUnit}`)}`}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: "85px" }}>{data?.status === 'NORMAL' && (data?.nextRecall ? `${toBuddhistYear(moment(data?.nextRecall), 'DD/MM/YYYY')} 07:00 ${t('TIME_UNIT')}` : '-') || t('STATUS_NO_ICON.INACTIVE')}</td>
                                <td style={{ fontSize: '9.5px', fontWeight: 500, textAlign: 'center', verticalAlign: 'center', width: "85px" }}>{data?.status === 'NORMAL' && (data?.lastRecall ? `${toBuddhistYear(moment(data?.lastRecall), 'DD/MM/YYYY HH:MM')} ${t('TIME_UNIT')}` : '-') || t('STATUS_NO_ICON.INACTIVE')}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </Box>
        </Box>
    )
}
